import { CheckoutContext } from '@context/CheckoutContext/checkout.context'
import React, { useContext, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import DurationSectionComponent from '../DurationSection/DurationSection.component'
import { Line } from '@views/Checkout/Checkout.style'
import { SectionTitle, TermsCheckBox, TermsContainer, Title } from './ConfirmationStep.style'
import { useStyletron } from 'parkdepot-shared/theme'
import FlexContainer from '@components/style/FlexContainer.style'
import Text from '@components/style/Text.style'
import OpeningHoursSection from '@views/Lot/components/OpeningHoursSection/OpeningHoursSection.component'
import NextButtonContainer from '@views/Checkout/style/NextButtonContainer.style'
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_SIZE } from 'parkdepot-shared/components/Buttons/types'
import { Link, useHistory } from 'react-router-dom'
import { guidelinesRoute } from '@views/Guidelines/route'
import { getDomainExtension } from '@utils/getDomainExtension'
import { checkoutRoutePaymentStep } from '@views/Checkout/route'
import ReactDOM from 'react-dom'

interface Props {
    bottomRef: React.RefObject<HTMLDivElement>
}

const ConfirmationStep: React.FC<Props> = ({ bottomRef }) => {
    const { checkout } = useContext(CheckoutContext)
    const history = useHistory()
    const { t } = useTranslation()
    const [css, theme] = useStyletron()
    const { lot } = checkout || {}
    const { address, city, name } = checkout?.lot || {}
    const [checked, setChecked] = useState(false)

    const onNextClick = () => {
        history.push(checkoutRoutePaymentStep())
    }

    return (
        <div>
            <Title>{t('checkout.yourBooking')}</Title>
            <FlexContainer alignitems="unset">
                <SectionTitle>{t('checkout.parkingSpace')}</SectionTitle>
                <div>
                    <Text className={css({ display: 'block' })} typography={theme.typography.ParagraphMedium}>
                        {name}
                    </Text>
                    <div>
                        <Text typography={theme.typography.ParagraphMedium}>{address}</Text>
                    </div>
                    <div>
                        <Text typography={theme.typography.ParagraphMedium}>{city}</Text>
                    </div>
                </div>
            </FlexContainer>
            <DurationSectionComponent checkout={checkout} />
            <FlexContainer margintop="1.6rem">
                <SectionTitle>{t('checkout.vehicle')}</SectionTitle>
                <div className={css({ display: 'flex', flexDirection: 'column', gap: '0.8rem' })}>
                    {checkout?.selectedVehicles?.map((vehicle) => (
                        <div key={vehicle.id}>
                            <Text typography={theme.typography.ParagraphMedium}>{vehicle.plate}</Text>
                        </div>
                    ))}
                </div>
            </FlexContainer>
            <FlexContainer margintop="1.6rem">
                <SectionTitle>{t('common.email')}</SectionTitle>
                <Text typography={theme.typography.ParagraphMedium}>{checkout?.email}</Text>
            </FlexContainer>
            <Line />
            <div>
                <SectionTitle>{t('checkout.openingHours')}</SectionTitle>
                <div
                    className={css({
                        [theme.mediaQuery.large]: {
                            marginTop: '-8px'
                        }
                    })}
                >
                    {lot && lot?.opening_hours && (
                        <OpeningHoursSection
                            openingHours={lot?.opening_hours.opening_hours_rrules}
                            showLabels={false}
                        />
                    )}
                </div>
            </div>
            {bottomRef.current &&
                ReactDOM.createPortal(
                    <NextButtonContainer>
                        <TermsContainer>
                            <TermsCheckBox
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                    setChecked(!checked)
                                }}
                            />
                            <span>
                                <Trans
                                    i18nKey="checkout.payment.terms"
                                    // TODO: create a mapper if we have more countries other than DE & AT
                                    values={{
                                        lot_fine: lot?.lot_country_code === 'DE' ? '40,00' : '80,00',
                                        currency: '€'
                                    }}
                                    components={{
                                        1: (
                                            <Link
                                                to={`${guidelinesRoute()}/gtc?lot=de-${
                                                    getDomainExtension() === 'at' ? 'AT' : 'DE'
                                                }`}
                                                target="_blank"
                                            />
                                        ),
                                        2: (
                                            <Link
                                                to={`${guidelinesRoute()}/withdrawal?lot=de-${
                                                    getDomainExtension() === 'at' ? 'AT' : 'DE'
                                                }`}
                                                target="_blank"
                                            />
                                        ),
                                        3: (
                                            <Link
                                                to={`${guidelinesRoute()}/privacy-policy?lot=de-${
                                                    getDomainExtension() === 'at' ? 'AT' : 'DE'
                                                }`}
                                                target="_blank"
                                            />
                                        )
                                    }}
                                ></Trans>
                            </span>
                        </TermsContainer>
                        {/* {!checked && (
                                      <ButtonCard disabled={true} onClick={() => undefined}>
                                          <TermsTextContainer>{t('checkout.payment.acceptTerms')}</TermsTextContainer>
                                      </ButtonCard>
                                  )} */}
                        <Button
                            id="continue-to-payment-step-button"
                            data-testid="continue-to-payment-step-button"
                            type="submit"
                            disabled={!checked}
                            $size={BUTTON_SIZE.medium}
                            $kind={BUTTON_KIND.primary}
                            onClick={onNextClick}
                            overrides={{
                                BaseButton: {
                                    style: {
                                        width: '100%'
                                    }
                                }
                            }}
                        >
                            <div>{t('checkout.payment.continueWithPayment')}</div>
                        </Button>
                    </NextButtonContainer>,
                    bottomRef.current
                )}
        </div>
    )
}

export default ConfirmationStep
